import React, { useEffect, useState, useRef } from 'react';
import {
  PlayCircleOutlined,
  StopOutlined,
  RedoOutlined,
  RocketOutlined,
  RightOutlined,
  LeftOutlined,
  UndoOutlined,
  PauseOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import { messageI18n } from '../../../helpers/i18n/toastI18n';
import {
  Play,
  CircleStopIcon as Stop,
  RotateCcw,
  Rocket,
  ArrowLeft,
  ArrowRight,
  Upload,
  UploadCloud,
  ChevronLeft,
  AlertTriangle,
} from 'lucide-react';
import VideoPlayer from './components/video-player/video-player';

// import FileLoaderService from 'platform/viewer/src/routes/Local/fileLoaderService';
import FileLoaderService from '../../../../routes/Local/fileLoaderService';
import { BlindSweepMode, UploaderType, Sweep } from '../../interfaces';
import {
  useCreateBlindInstance,
  usePredictionBlind,
} from '../../../helpers/api/instance/instance';
import sweep1 from './assets/sweep1.png';
import sweep2 from './assets/sweep2.png';
import sweep3 from './assets/sweep3.png';
import sweep4 from './assets/sweep4.png';
import sweep5 from './assets/sweep5.png';
import sweep6 from './assets/sweep6.png';
import sweep7 from './assets/sweep7.png';
import {
  processDicom,
  isVideoResult,
  isFrameResult,
  isUnsupportedTransferSyntax,
  getTransferSyntaxName,
  DicomProcessResult,
  DicomFrameResult,
  DicomVideoResult,
} from './utils/dicomProcessor';
import DevicesModal from '../devices-modal';
import { useVideoProcessing } from '../hooks/useVideoProcessing';
import { FaSpinner } from 'react-icons/fa6';
import BsExamAnalyticsDrawer from '../BsExamAnalytics/BsExamAnalyticsDrawer';
import { isBsExamAnalyticsEnabled } from '../../../helpers/api/blind-sweep/bs-analytics';
import Report from '../../../../../../ui/src/components/MeasurementTable/Components/Report/report.page';
import BlindSweepReport from '../BlindSweepReport/BlindSweepReportTwo';

interface SweepProps {
  mode?: BlindSweepMode;
  sweeps: Sweep[];
  setSweeps: (sweeps: Sweep[]) => void;
  onPrevious?: () => void;
  showReportModal?: () => void;
  showNavigation?: boolean;
  isPredicted?: boolean;
  setStudyReport: (studyReport: any) => void;
  onRefreshUrls?: () => void;
  isRefreshingUrls?: boolean;
  urlExpiryTime?: number | null;
  isReportLoading?: boolean;
  predictionResults?: PredictionResults;
}
interface GlobalState {
  inputDevices: any[];
  isStart: boolean;
  deviceId: string | null;
  optionValueDevice: string | null;
}
enum MediaType {
  USER_MEDIA = 'UserMedia',
  DISPLAY_MEDIA = 'DisplayMedia',
}
const mediaTypeMap = new Map([
  ['Share', MediaType.DISPLAY_MEDIA],
  ['USB', MediaType.USER_MEDIA],
]);

function SweepItems({
  mode = BlindSweepMode.UPLOAD,
  sweeps,
  setSweeps,
  onPrevious,
  showNavigation = false,
  isPredicted,
  setStudyReport,
  onRefreshUrls,
  isRefreshingUrls,
  urlExpiryTime,
  showReportModal,
  isReportLoading,
  predictionResults,
}: SweepProps) {
  console.log('isPredicted', isPredicted);
  const [showModalReport, setShowModalReport] = useState(false);
  const showModal = (study_id: string) => {
    setShowModalReport(true);
  };
  const handleCancel = () => {
    setShowModalReport(false);
  };
  const [activeSweep, setActiveSweep] = useState<string | null>(null);
  // const [stream, setStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [selectedSweep, setSelectedSweep] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadSweep, setCurrentUploadSweep] = useState<string | null>(
    null
  );
  const [uploaderType, setUploaderType] = useState<UploaderType | null>(null);
  const [referenceImageSize, setReferenceImageSize] = useState<
    'small' | 'medium' | 'large'
  >('small');
  const [processingMessage, setProcessingMessage] = useState<string>('');
  let StudyUID = new URLSearchParams(location.search).get('StudyInstanceUIDs');
  // const videoRef = useRef<HTMLVideoElement>(null);
  const recordedVideoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const timerEndTimeRef = useRef<number | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [deviceMediaType, setDeviceMediaType] = useState(
    MediaType.DISPLAY_MEDIA
  );
  const [bool, setBool] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  const serieInstanceUid =
    sweeps.find((s) => s.serie_instance_uid)?.serie_instance_uid ?? null;
  const showBsAnalytics = isBsExamAnalyticsEnabled() && Boolean(serieInstanceUid);

  const [globalState, setGlobalState] = useState<GlobalState>({
    inputDevices: [],
    isStart: false,
    deviceId: null,
    optionValueDevice: null,
  });
  const {
    videoStream,
    videoRef,
    processingCanvasRef,
    processedStreamRef,
    isVideoLoading,
    createCroppedStream,
    startVideoStream,
    stopVideoStream,
    setIsVideoLoading,
    stream,
  } = useVideoProcessing(globalState?.deviceId);
  useEffect(() => {
    if (globalState?.isStart) {
      startVideoStream(globalState.deviceId, globalState.optionValueDevice);
    }

    return () => {
      stopVideoStream();
    };
  }, [globalState?.isStart, bool]);

  useEffect(() => {
    if (!isPredicted || !showBsAnalytics || !serieInstanceUid) {
      return;
    }
    const key = `bs-analytics-auto-opened-${serieInstanceUid}`;
    if (localStorage.getItem(key)) {
      return;
    }
    localStorage.setItem(key, '1');
    setAnalyticsOpen(true);
  }, [isPredicted, showBsAnalytics, serieInstanceUid]);
  console.log('videoStream', videoStream);
  console.log('stream', stream);
  console.log('globalState', globalState);
  console.log('videoRef', videoRef);
  const mediaTypeSwitcher = () => {
    mediaTypeMap.forEach((mediaType, option) => {
      if (globalState.optionValueDevice === option) {
        setDeviceMediaType(mediaType);
      }
    });
  };

  const onSuccessBlind = (response: any, data: any) => {
    console.log('onSuccessBlind:', response?.data);

    if (response?.status_code === 200) {
      setStudyReport((prev) => ({
        ...prev,
        report_content: response?.data?.report_content,
        blindsweep_urls: response?.data?.blindsweep_urls,
      }));
    }
  };
  const onErrorBlind = (error: any, data: any) => {
    console.log('onErrorBlind:', error, data);
  };
  const { mutate: predictBlindStudy, isPending: predictBlindStudyLoading } =
    usePredictionBlind(onSuccessBlind, onErrorBlind);
  const onSuccess = (response: any, data: any) => {
    if (response?.status_code === 201) {
      messageI18n.success('sweep.blindInstanceCreated');
      console.log('onSuccess:', response?.data);
      const sweepId =
        response?.data?.instance?.sweep_id?.replace(/SWEEP/i, '') ||
        response?.data?.instance?.sweep_id;
      console.log('onSuccess: 1 :', sweepId);

      if (sweepId && response.data?.signed_url) {
        setSweeps((prevSweeps) => {
          const updatedSweeps = prevSweeps.map((s) => {
            if (s.id === sweepId) {
              return {
                ...s,
                video_url: s.isSupported
                  ? s.video_url
                  : response.data.signed_url,
                completed: true,
                error: false,
                sop_instance_uid:
                  response.data.instance?.sop_instance_uid || null,
                serie_instance_uid:
                  response.data.instance?.serie_instance_uid || null,
                prediction_state:
                  response.data.instance?.prediction_state || null,
                isUploading: false,
              };
            }
            return s;
          });
          return updatedSweeps;
        });
        setIsUploading(false);
        // setSelectedSweep(sweepId);
        setCurrentUploadSweep(sweepId);
      }
    }
  };

  const onError = (error: any, data: any) => {
    console.log('srReport:', error, data);
    messageI18n.error('sweep.blindInstanceCreateError');
  };
  const { mutate: createBlindInstance, isPending: createBlindInstanceLoading } =
    useCreateBlindInstance(onSuccess, onError, false);

  // useEffect(() => {
  //   if (mode === BlindSweepMode.RECORD) {
  //     const initializeStream = async () => {
  //       try {
  //         const newStream = await navigator.mediaDevices.getDisplayMedia({
  //           video: { frameRate: { ideal: 30 } },
  //           audio: false,
  //         });
  //         setStream(newStream);
  //         if (videoRef.current) {
  //           videoRef.current.srcObject = newStream;
  //         }
  //       } catch (err) {
  //         console.error('Error sharing screen:', err);
  //       }
  //     };
  //     initializeStream();
  //   }
  //   return () => {
  //     if (stream) {
  //       stream.getTracks().forEach(track => track.stop());
  //     }
  //     if (animationFrameRef.current) {
  //       cancelAnimationFrame(animationFrameRef.current);
  //     }
  //     if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
  //       mediaRecorderRef.current.stop();
  //     }
  //   };
  // }, [mode]);

  useEffect(() => {
    if (selectedSweep) {
      const sweep = sweeps.find((s) => s.id === selectedSweep);
      if (sweep?.video_url && recordedVideoRef.current) {
        recordedVideoRef.current.src = sweep.video_url;
        recordedVideoRef.current
          .play()
          .catch((err) => console.error('Error playing video:', err));
      }
    }
  }, [selectedSweep, sweeps]);

  useEffect(() => {
    if (!selectedSweep && sweeps.length > 0) {
      setSelectedSweep(sweeps[0].id);
    }
  }, [selectedSweep, sweeps]);

  const startRecording = (id: string) => {
    // if (!globalState?.isStart) {
    //   setShowDeviceModal(true);
    //   return;
    // }
    if (!videoStream) {
      console.log('No stream available for recording');
      return;
    }
    const sweep = sweeps.find((s) => s.id === id);
    if (!sweep) return;
    const durationInSeconds = sweep.timer;
    recordingStartTimeRef.current = performance.now();
    recordingChunksRef.current = [];
    try {
      const mediaRecorder = new MediaRecorder(videoStream, {
        mimeType: 'video/webm;codecs=vp8,opus',
        videoBitsPerSecond: 1000000,
      });
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };
      mediaRecorder.onstop = async () => {
        const videoBlob = new Blob(recordingChunksRef.current, {
          type: 'video/webm',
        });
        const videoUrl = URL.createObjectURL(videoBlob);
        let actualDuration = 0;
        if (recordingStartTimeRef.current) {
          actualDuration =
            (performance.now() - recordingStartTimeRef.current) / 1000;
          console.log(
            `Actual recording duration: ${actualDuration.toFixed(2)} seconds`
          );
        }
        setSweeps((prevSweeps) =>
          prevSweeps.map((s) =>
            s.id === id
              ? {
                ...s,
                recording: false,
                completed: true,
                video_url: videoUrl,
                timer: 0,
                displayTimer: 0,
              }
              : s
          )
        );

        // Upload the recorded video
        const formData = new FormData();
        formData.append(
          'file',
          new File([videoBlob], `sweep_${id}.webm`, { type: 'video/webm' })
        );
        formData.append(
          'instance',
          JSON.stringify({
            study_instance_uid: sweeps[0].study_instance_uid,
            sweep_id: id,
            patient_id: 1,
          })
        );
        await createBlindInstance(formData);

        setIsRecording(false);
        setActiveSweep(null);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        mediaRecorderRef.current = null;
        timerEndTimeRef.current = null;
        recordingStartTimeRef.current = null;
      };
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        stopRecording();
      };
      mediaRecorder.start(100);
      mediaRecorderRef.current = mediaRecorder;
      setSweeps((prevSweeps) =>
        prevSweeps.map((s) =>
          s.id === id ? { ...s, recording: true, displayTimer: 10 } : s
        )
      );
      setActiveSweep(id);
      setSelectedSweep(id);
      setIsRecording(true);
      const startTime = performance.now();
      timerEndTimeRef.current = startTime + durationInSeconds * 1000;
      const updateTimer = (timestamp: number) => {
        if (!timerEndTimeRef.current) return;
        const remainingMs = timerEndTimeRef.current - timestamp;
        const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
        const displaySec = Math.max(0, remainingSec - 1);
        setSweeps((prevSweeps) =>
          prevSweeps.map((sweep) => {
            if (sweep.id === id && sweep.recording) {
              return {
                ...sweep,
                timer: remainingSec,
                displayTimer: displaySec,
              };
            }
            return sweep;
          })
        );
        if (remainingMs <= 0) {
          if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === 'recording'
          ) {
            mediaRecorderRef.current.stop();
          }
          return;
        }
        animationFrameRef.current = requestAnimationFrame(updateTimer);
      };
      animationFrameRef.current = requestAnimationFrame(updateTimer);
      const exactDuration = durationInSeconds * 1000;
      setTimeout(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === 'recording'
        ) {
          console.log(
            `Stopping recording after precise timing: ${exactDuration}ms`
          );
          mediaRecorderRef.current.stop();
        }
      }, exactDuration);
    } catch (error) {
      console.error('Error setting up MediaRecorder:', error);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.stop();
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    timerEndTimeRef.current = null;
    recordingStartTimeRef.current = null;
  };

  const resetSweep = (id: string) => {
    const sweep = sweeps.find((s) => s.id === id);
    if (sweep?.video_url && sweep.video_url.startsWith('blob:')) {
      URL.revokeObjectURL(sweep.video_url);
    }

    setSweeps((prevSweeps) =>
      prevSweeps.map((sweep) =>
        sweep.id === id
          ? {
            ...sweep,
            recording: false,
            timer: 11,
            displayTimer: 10,
            completed: false,
            video_url: null,
            error: false,
            errorMessage: null,
          }
          : sweep
      )
    );

    if (activeSweep === id) {
      stopRecording();
    }

    if (selectedSweep === id) {
      setSelectedSweep(null);
    }

    // If all sweeps are now reset, we can also reset the uploader type
    const anyCompletedSweeps = sweeps.some((s) => s.id !== id && s.completed);
    if (!anyCompletedSweeps) {
      setUploaderType(null);
    }
  };

  const resetAll = () => {
    sweeps.forEach((sweep) => {
      if (sweep.video_url && sweep.video_url.startsWith('blob:')) {
        URL.revokeObjectURL(sweep.video_url);
      }
    });

    stopRecording();

    // Reset the uploader type when resetting all sweeps
    setUploaderType(null);

    setSweeps((prevSweeps) =>
      prevSweeps.map((sweep) => ({
        ...sweep,
        recording: false,
        timer: 11,
        displayTimer: 10,
        completed: false,
        video_url: null,
        error: false,
        errorMessage: null,
      }))
    );
    setActiveSweep(null);
    setSelectedSweep(null);
    setIsRecording(false);
  };

  const toggleRecording = (id: string) => {
    const sweep = sweeps.find((s) => s.id === id);
    if (!sweep || sweep.completed || isRecording) return;

    startRecording(id);
  };

  const findNextAvailableSweep = () => {
    return sweeps.find((s) => !s.completed);
  };

  const startNextRecording = () => {
    if (isRecording) {
      stopRecording();
      return;
    }

    const nextSweep = findNextAvailableSweep();
    if (nextSweep) {
      toggleRecording(nextSweep.id);
    }
  };

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    sweepId: string
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    // Determine file type if it's the first upload
    if (uploaderType === null) {
      // Check if it's a DICOM file by extension or MIME type
      const isDicom =
        file.name.toLowerCase().endsWith('.dcm') ||
        file.type === 'application/dicom';

      // Only set uploader type if it's a valid file type (DICOM or video)
      if (isDicom) {
        setUploaderType(UploaderType.DICOM);
      } else if (file.type.startsWith('video/')) {
        setUploaderType(UploaderType.VIDEO);
      } else {
        // Invalid file type, don't proceed
        console.error(
          'Invalid file type. Only DICOM and video files are accepted.'
        );
        return;
      }
    } else {
      // Check if the file type matches the current uploader type
      const isDicom =
        file.name.toLowerCase().endsWith('.dcm') ||
        file.type === 'application/dicom';
      const isVideo = file.type.startsWith('video/');

      if (
        (uploaderType === UploaderType.DICOM && !isDicom) ||
        (uploaderType === UploaderType.VIDEO && !isVideo)
      ) {
        console.error(
          `Invalid file type. Only ${uploaderType === UploaderType.VIDEO ? 'video' : 'DICOM'
          } files are accepted after the first upload.`
        );
        return;
      }
    }

    setCurrentUploadSweep(sweepId);
    setIsUploading(true);
    setUploadProgress(0);

    // Get the sweep name to use in the filename
    const sweep = sweeps.find((s) => s.id === sweepId);
    const sweepName =
      sweep?.name?.replace(/\s+/g, '').toLowerCase() || `sweep${sweepId}`;

    // Get file extension
    const fileExtension = file.name.split('.').pop() || '';

    // Create a new file with the desired name
    const renamedFile = new File([file], `${sweepName}.${fileExtension}`, {
      type: file.type,
    });

    if (
      uploaderType === UploaderType.VIDEO ||
      (uploaderType === null && file.type.startsWith('video/'))
    ) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
      const browserCompatibleFormats = ['mp4', 'webm', 'ogg', 'mov'];
      const isLikelyBrowserCompatible =
        browserCompatibleFormats.includes(fileExtension) ||
        file.type.startsWith('video/mp4') ||
        file.type.startsWith('video/webm') ||
        file.type.startsWith('video/ogg');
      console.log('isLikelyBrowserCompatible', isLikelyBrowserCompatible);
      handleVideoUpload(file, sweepId, isLikelyBrowserCompatible);
      const formData = new FormData();
      formData.append('file', renamedFile);
      formData.append(
        'instance',
        JSON.stringify({
          study_instance_uid: sweeps[0].study_instance_uid,
          sweep_id: sweepId,
          patient_id: 1,
        })
      );
      await createBlindInstance(formData);
    } else if (
      uploaderType === UploaderType.DICOM ||
      (uploaderType === null &&
        (file.name.toLowerCase().endsWith('.dcm') ||
          file.type === 'application/dicom'))
    ) {
      handleDicomUpload(file, sweepId);
    }
  };

  const handleVideoUpload = (
    file: File,
    sweepId: string,
    isLikelyBrowserCompatible
  ) => {
    const videoUrl = URL.createObjectURL(file);
    if (isLikelyBrowserCompatible) {
      setSweeps((prevSweeps) =>
        prevSweeps.map((s) =>
          s.id === sweepId
            ? {
              ...s,
              completed: true,
              video_url: videoUrl,
              error: false,
              isUploading: false,
              isSupported: true,
            }
            : s
        )
      );
      setSelectedSweep(sweepId);
      setCurrentUploadSweep(sweepId);
      setIsUploading(false);
    } else {
      setSweeps((prevSweeps) =>
        prevSweeps.map((s) =>
          s.id === sweepId
            ? {
              ...s,
              completed: false,
              video_url: null,
              error: false,
              isUploading: true,
              isSupported: false,
            }
            : s
        )
      );
    }
  };

  const handleDicomUpload = async (file: File, sweepId: string) => {
    try {
      // Set initial state - uploading
      setSweeps((prevSweeps) =>
        prevSweeps.map((s) =>
          s.id === sweepId
            ? {
              ...s,
              completed: false,
              video_url: null,
              error: false,
              isUploading: true,
              errorMessage: null,
            }
            : s
        )
      );

      setIsUploading(true);
      setCurrentUploadSweep(sweepId);
      setProcessingMessage(`Processing ${file.name}...`);
      setUploadProgress(10);

      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'instance',
        JSON.stringify({
          study_instance_uid: sweeps[0].study_instance_uid,
          sweep_id: sweepId,
          patient_id: 1,
        })
      );

      // Upload the DICOM file to the backend
      setUploadProgress(30);
      await createBlindInstance(formData);
      // setUploadProgress(90);

      // setUploadProgress(100);
      // setSelectedSweep(sweepId);

      // // Reset states after a short delay to show 100% completion
      // setTimeout(() => {
      //   setIsUploading(false);
      //   setCurrentUploadSweep(null);
      //   setProcessingMessage('');
      //   setUploadProgress(0);
      // }, 1000);
    } catch (error) {
      console.error('Error processing DICOM file:', error);

      // Update sweep with error state
      setSweeps((prevSweeps) =>
        prevSweeps.map((s) =>
          s.id === sweepId
            ? {
              ...s,
              completed: false,
              video_url: null,
              error: true,
              isUploading: false,
              errorMessage:
                error.response?.data?.message ||
                'Error processing DICOM file. Please try another file.',
            }
            : s
        )
      );

      // Reset states
      setUploadProgress(0);
      setIsUploading(false);
      setCurrentUploadSweep(null);
      setProcessingMessage('');
    }
  };

  const triggerFileUpload = (sweepId: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
      fileInputRef.current.dataset.sweepId = sweepId;
    }
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const sweepId = fileInputRef.current?.dataset.sweepId || '';
    if (sweepId) {
      await handleFileSelect(e, sweepId);
    }
  };

  const allSweepsCompleted = !sweeps.some((s) => !s.completed);

  const currentSweep = selectedSweep
    ? sweeps.find((s) => s.id === selectedSweep)
    : null;
  const showRecordedVideo = selectedSweep && currentSweep?.video_url;
  const handleSweepClick = (id: string) => {
    if (isRecording) return;
    setSelectedSweep(id);
    if (mode === BlindSweepMode.UPLOAD) {
      const sweep = sweeps.find((s) => s.id === id);
      if (sweep?.video_url) {
      }
    }
  };

  const handlePredictBlindStudy = () => {
    predictBlindStudy({
      prediction_type: 'STUDY',
      study_uid: StudyUID,
    });
  };
  const API = process.env.BACKEND_URL?.replace(/\/api\/v1$/, '');

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-full w-full flex-col rounded-lg bg-white shadow-lg">
        <div className="flex min-h-0 flex-1 flex-col gap-3 p-2 sm:gap-4 sm:p-3 md:gap-5 md:p-4 lg:flex-row lg:gap-6">
          <Report
            StudyUID={StudyUID}
            ohifViewer={false}
            aiAccelerate={true}
            isOpen={showModalReport}
            onClose={handleCancel}
            blind={<><BlindSweepReport predictionResults={predictionResults} /></>}
          />
          {/* <div className="order-2 flex h-auto w-full flex-col lg:order-1 lg:h-full lg:w-[250px] xl:w-[300px]">
            <div className="mb-2 flex flex-grow flex-col overflow-hidden rounded-lg bg-blue-100 p-2 sm:p-3 md:p-4">
              <h2 className="mb-2 flex-shrink-0 rounded bg-blue-200 py-1 text-center text-base font-semibold text-blue-800 sm:py-2 sm:text-lg md:text-xl">
                Sweeps
              </h2>
              <div className="grid flex-1 grid-cols-2 content-start gap-2 overflow-auto p-2 sm:grid-cols-3 lg:grid-cols-1">
                {sweeps.map((sweep) => (
                  <div
                    key={sweep.id}
                    className={`flex items-center justify-between rounded-lg bg-white p-1 sm:p-2 ${
                      selectedSweep === sweep.id ? 'ring-2 ring-blue-500' : ''
                    } ${sweep.completed ? 'bg-blue-50' : ''}`}
                    onClick={() => !isRecording && handleSweepClick(sweep.id)}
                  >
                    <span
                      className={`cursor-pointer text-sm sm:text-base md:text-lg ${
                        sweep.completed ? 'font-semibold' : ''
                      }`}
                    >
                      {sweep.name} {sweep.completed && '✓'}
                    </span>
                    <div className="flex items-center gap-1 sm:gap-2">

                      {mode === BlindSweepMode.RECORD ? (
                        <Button
                          size="sm"
                          variant={
                            sweep.completed || isRecording
                              ? 'secondary'
                              : 'default'
                          }
                          className="p-1 sm:p-2"
                          onClick={() =>
                            !sweep.completed &&
                            !isRecording &&
                            toggleRecording(sweep.id)
                          }
                          disabled={sweep.completed || isRecording}
                        >
                          {sweep.recording ? (
                            <Stop className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <Play className="h-3 w-3 sm:h-4 sm:w-4" />
                          )}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant={
                            sweep.completed || isUploading || isRecording
                              ? 'secondary'
                              : 'default'
                          }
                          className="p-1 sm:p-2"
                          onClick={() =>
                            !sweep.completed &&
                            !isUploading &&
                            !isRecording &&
                            triggerFileUpload(sweep.id)
                          }
                          disabled={
                            sweep.completed || isUploading || isRecording
                          }
                        >
                          <Upload className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                      {mode === BlindSweepMode.RECORD && (
                        <span
                          className={`w-8 text-center font-mono text-xs sm:w-12 sm:text-sm ${
                            mode === BlindSweepMode.RECORD
                              ? sweep.displayTimer === 0
                                ? 'font-bold text-green-600'
                                : ''
                              : ''
                          }`}
                        >
                          {mode === BlindSweepMode.RECORD
                            ? `${sweep.displayTimer
                                .toString()
                                .padStart(2, '0')}s`
                            : currentUploadSweep === sweep.id
                              ? `${uploadProgress}%`
                              : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
          </div> */}

          {/* Video area - takes priority on all screens */}
          <div className="order-1 flex h-full flex-1 flex-col space-y-2 sm:space-y-3 md:space-y-4 lg:order-2">
            <div className="mb-1 flex flex-wrap justify-between gap-2 sm:mb-2">
              <div className="flex gap-2">
                {/* <Button
                  variant="outline"
                  onClick={resetAll}
                  className="text-xs sm:text-sm"
                  disabled={isRecording}
                >
                  Reset All
                </Button> */}
                {/* {onRefreshUrls && (
                  <Button
                    variant="outline"
                    onClick={onRefreshUrls}
                    disabled={isRefreshingUrls || isRecording}
                    className="text-xs sm:text-sm"
                  >
                    {isRefreshingUrls ? 'Refreshing...' : 'Refresh URLs'}
                  </Button>
                )} */}
              </div>
              {mode === BlindSweepMode.RECORD ? (
                <Button
                  variant={isRecording ? 'destructive' : 'default'}
                  onClick={startNextRecording}
                  disabled={allSweepsCompleted && !isRecording}
                  className="text-xs sm:text-sm"
                >
                  {isRecording ? 'Stop Recording' : 'Start Recording'}
                </Button>
              ) : (
                <div className="flex items-center gap-1 sm:gap-2">
                  {/* <span className="hidden text-xs text-gray-500 sm:inline sm:text-sm">
                    {uploaderType === UploaderType.VIDEO
                      ? 'Video Upload Mode'
                      : uploaderType === UploaderType.DICOM
                        ? 'DICOM Upload Mode'
                        : 'Upload Mode'}
                  </span>
                  <Button
                    variant="default"
                    onClick={() => {
                      const nextSweep = findNextAvailableSweep();
                      if (nextSweep) triggerFileUpload(nextSweep.id);
                    }}
                    disabled={isUploading || allSweepsCompleted || isRecording}
                    className="text-xs sm:text-sm"
                  >
                    Upload{' '}
                    {uploaderType === UploaderType.VIDEO
                      ? 'Video'
                      : uploaderType === UploaderType.DICOM
                        ? 'DICOM'
                        : 'File'}
                  </Button> */}
                  <Button
                    type="primary"
                    size="large"
                    className="bg-blue-600 hover:bg-blue-700 border-blue-700 flex items-center space-x-2 text-xs sm:text-sm md:text-base"
                    onClick={showModal}
                    disabled={isReportLoading}
                  >
                    {isReportLoading ? <FaSpinner className="animate-spin mr-2" /> : <></>}
                    Report
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    className="bg-blue-600 hover:bg-blue-700 border-blue-700 flex items-center space-x-2 text-xs sm:text-sm md:text-base"
                    onClick={showReportModal}
                    disabled={isReportLoading}
                  >
                    {isReportLoading ? <FaSpinner className="animate-spin mr-2" /> : <></>}
                    Report BS
                  </Button>
                  {showBsAnalytics ? (
                    <Button
                      size="large"
                      className="text-xs sm:text-sm md:text-base"
                      onClick={() => setAnalyticsOpen(true)}
                    >
                      BS Pipeline Debug
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
            <div className="relative aspect-video flex-1 overflow-hidden rounded-lg bg-blue-100">
              <div className="relative h-full w-full">
                <VideoPlayer
                  src={currentSweep?.video_url || ''}
                  isDarkMode={true}
                  showRecordedVideo={showRecordedVideo}
                />
                <video
                  ref={videoRef}
                  className="absolute inset-0 h-full w-full"
                  autoPlay
                  muted
                  style={{
                    display:
                      !showRecordedVideo && mode === BlindSweepMode.RECORD
                        ? 'block'
                        : 'none',
                  }}
                />
                {mode === BlindSweepMode.RECORD && isRecording && (
                  <div className="absolute top-2 right-2 z-20 animate-pulse rounded-md bg-blue-500 px-2 py-1 text-xs text-white sm:top-4 sm:right-4 sm:px-3 sm:py-2 sm:text-sm">
                    <div className="flex items-center">
                      <span>
                        Recording{' '}
                        {activeSweep &&
                          sweeps.find((s) => s.id === activeSweep)?.name}
                      </span>
                      <span className="ml-1 font-mono">
                        {activeSweep &&
                          sweeps.find((s) => s.id === activeSweep)
                            ?.displayTimer}
                        s
                      </span>
                    </div>
                  </div>
                )}

                {mode === BlindSweepMode.RECORD && (
                  <div
                    className={`absolute ${referenceImageSize === 'large'
                        ? 'inset-0 z-10'
                        : referenceImageSize === 'medium'
                          ? 'top-14 right-2 z-10 h-32 w-32 sm:top-16 sm:right-4'
                          : 'top-14 right-2 z-10 h-24 w-24 sm:top-16 sm:right-4'
                      }`}
                  >
                    <img
                      src={
                        selectedSweep &&
                          sweeps.find((s) => s.id === selectedSweep)?.name ===
                          'Sweep 1'
                          ? sweep1
                          : selectedSweep &&
                            sweeps.find((s) => s.id === selectedSweep)
                              ?.name === 'Sweep 2'
                            ? sweep2
                            : selectedSweep &&
                              sweeps.find((s) => s.id === selectedSweep)
                                ?.name === 'Sweep 3'
                              ? sweep3
                              : selectedSweep &&
                                sweeps.find((s) => s.id === selectedSweep)
                                  ?.name === 'Sweep 4'
                                ? sweep4
                                : selectedSweep &&
                                  sweeps.find((s) => s.id === selectedSweep)
                                    ?.name === 'Sweep 5'
                                  ? sweep5
                                  : selectedSweep &&
                                    sweeps.find((s) => s.id === selectedSweep)
                                      ?.name === 'Sweep 6'
                                    ? sweep6
                                    : selectedSweep &&
                                      sweeps.find(
                                        (s) => s.id === selectedSweep
                                      )?.name === 'Sweep 7'
                                      ? sweep7
                                      : activeSweep &&
                                        sweeps.find(
                                          (s) => s.id === activeSweep
                                        )?.name === 'Sweep 1'
                                        ? sweep1
                                        : activeSweep &&
                                          sweeps.find(
                                            (s) => s.id === activeSweep
                                          )?.name === 'Sweep 2'
                                          ? sweep2
                                          : activeSweep &&
                                            sweeps.find(
                                              (s) => s.id === activeSweep
                                            )?.name === 'Sweep 3'
                                            ? sweep3
                                            : activeSweep &&
                                              sweeps.find(
                                                (s) => s.id === activeSweep
                                              )?.name === 'Sweep 4'
                                              ? sweep4
                                              : activeSweep &&
                                                sweeps.find(
                                                  (s) => s.id === activeSweep
                                                )?.name === 'Sweep 5'
                                                ? sweep5
                                                : activeSweep &&
                                                  sweeps.find(
                                                    (s) =>
                                                      s.id === activeSweep
                                                  )?.name === 'Sweep 6'
                                                  ? sweep6
                                                  : activeSweep &&
                                                    sweeps.find(
                                                      (s) =>
                                                        s.id === activeSweep
                                                    )?.name === 'Sweep 7'
                                                    ? sweep7
                                                    : sweep1 // Default to sweep1 if no active or selected sweep
                      }
                      alt="Reference"
                      onClick={() => {
                        if (referenceImageSize === 'small') {
                          setReferenceImageSize('medium');
                        } else if (referenceImageSize === 'medium') {
                          setReferenceImageSize('large');
                        } else {
                          setReferenceImageSize('small');
                        }
                      }}
                      className={`${referenceImageSize === 'large'
                          ? 'h-full w-full bg-white bg-opacity-70 object-contain'
                          : 'h-full w-full rounded-md border-2 border-white bg-white bg-opacity-50 object-contain shadow-md'
                        } cursor-pointer`}
                    />
                  </div>
                )}
                {mode === BlindSweepMode.UPLOAD && !showRecordedVideo && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {selectedSweep &&
                      sweeps.find((s) => s.id === selectedSweep)?.error ? (
                      <div className="flex flex-col items-center justify-center p-4 text-center">
                        <div className="mb-4 rounded-full bg-red-100 p-3">
                          <StopOutlined className="text-2xl text-red-500" />
                        </div>
                        <p className="mb-2 text-lg font-semibold text-red-600">
                          Error
                        </p>
                        <p className="mb-4 text-gray-700">
                          {sweeps.find((s) => s.id === selectedSweep)
                            ?.errorMessage ||
                            'An error occurred with this file.'}
                        </p>
                        <Button
                          variant="outline"
                          onClick={() => resetSweep(selectedSweep)}
                          className="text-xs sm:text-sm"
                        >
                          Reset and Try Again
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="relative w-full h-full flex flex-col items-center justify-center">
                          <div className="absolute inset-0 backdrop-blur-sm bg-white/50 flex items-center justify-center z-10">
                            <div className="bg-white p-4 rounded-lg shadow-lg border border-red-200 text-center">
                              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                              <p className="font-medium text-gray-800">This feature is currently under maintenance</p>
                              <p className="text-sm text-gray-600 mt-1">Please check back later</p>
                            </div>
                          </div>
                          <UploadCloud className="mb-2 h-10 w-10 text-blue-300 sm:mb-4 sm:h-16 sm:w-16" />
                          <p className="px-2 text-center text-sm text-blue-600 sm:text-base md:text-lg">
                            {isUploading
                              ? `Processing ${uploaderType === UploaderType.VIDEO
                                ? 'video'
                                : uploaderType === UploaderType.DICOM
                                  ? 'DICOM'
                                  : 'file'
                              } ...`
                              : `Select a sweep and upload a ${uploaderType === UploaderType.VIDEO
                                ? 'video'
                                : uploaderType === UploaderType.DICOM
                                  ? 'DICOM'
                                  : 'video or DICOM'
                              } file`}
                          </p>
                          {processingMessage && isUploading && (
                            <p className="mt-1 text-xs text-blue-500 sm:text-sm">
                              {processingMessage}
                            </p>
                          )}
                          {isUploading && (
                            <div className="mt-2 h-1 w-32 overflow-hidden rounded-full bg-blue-100 sm:mt-4 sm:h-2 sm:w-64">
                              <div
                                className="h-full bg-blue-500 transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              <canvas
                ref={canvasRef}
                className="hidden"
                width="640"
                height="480"
              />
            </div>
            <div className="mt-1 flex justify-between sm:mt-2">
              {/* <Button
                variant="outline"
                onClick={() => {
                  if (selectedSweep && !isRecording) {
                    const currentIndex = sweeps.findIndex(
                      (s) => s.id === selectedSweep
                    );
                    if (currentIndex > 0) {
                      const prevSweep = sweeps[currentIndex - 1];
                      setSelectedSweep(prevSweep.id);
                    }
                  }
                }}
                className="flex items-center justify-center text-xs sm:text-sm"
                disabled={
                  !selectedSweep ||
                  sweeps.findIndex((s) => s.id === selectedSweep) === 0 ||
                  isRecording
                }
              >
                <ArrowLeft className="mr-1 h-3 w-3 sm:mr-2 sm:h-4 sm:w-4" />
                Previous Sweep
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (selectedSweep && !isRecording) {
                    const currentIndex = sweeps.findIndex(
                      (s) => s.id === selectedSweep
                    );
                    if (currentIndex < sweeps.length - 1) {
                      const nextSweep = sweeps[currentIndex + 1];
                      setSelectedSweep(nextSweep.id);
                    }
                  }
                }}
                className="flex items-center justify-center text-xs sm:text-sm"
                disabled={
                  !selectedSweep ||
                  sweeps.findIndex((s) => s.id === selectedSweep) ===
                    sweeps.length - 1 ||
                  isRecording
                }
              >
                Next Sweep
                <ArrowRight className="ml-1 h-3 w-3 sm:ml-2 sm:h-4 sm:w-4" />
              </Button> */}
            </div>
            {/* {urlExpiryTime && (
              <div className="absolute top-2 left-2 z-50 bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-sm">
                URLs expire at: {new Date(urlExpiryTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            )} */}
          </div>
        </div>
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        className="hidden"
        accept={
          uploaderType === UploaderType.VIDEO
            ? 'video/*'
            : uploaderType === UploaderType.DICOM
              ? '.dcm,application/dicom'
              : 'video/*,.dcm,application/dicom'
        }
        disabled={isRecording}
      />
      <DevicesModal
        setShowDeviceModal={setShowDeviceModal}
        showDeviceModal={showDeviceModal}
        startVideoStream={startVideoStream}
        mediaTypeSwitcher={mediaTypeSwitcher}
        globalState={globalState}
        setGlobalState={setGlobalState}
        inputDevices={globalState.inputDevices}
        api={API}
        handleNext={() => {
          setShowDeviceModal(false);
          setBool(false);

          setGlobalState((prevState) => ({
            ...prevState,
            isStart: true,
          }));
        }}
        setDeviceId={(value) =>
          setGlobalState((prevState) => ({
            ...prevState,
            deviceId: value,
          }))
        }
        setInputDevices={(value) =>
          setGlobalState((prevState) => ({
            ...prevState,
            inputDevices: value,
          }))
        }
      />
      {showBsAnalytics && serieInstanceUid ? (
        <BsExamAnalyticsDrawer
          open={analyticsOpen}
          onClose={() => setAnalyticsOpen(false)}
          serieInstanceUid={serieInstanceUid}
        />
      ) : null}
    </div>
  );
}

export default SweepItems;
